import { useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { PARTNERS, CLIENT_TESTIMONIALS } from "@/data/content";

// Renders any coloured logo (transparent or white-bg) as a white mark on dark.
function ClientLogo({ src, alt, className = "" }) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className={`object-contain ${className}`}
      style={{ filter: "grayscale(1) invert(1) brightness(1.7) contrast(1.1)", mixBlendMode: "screen" }}
    />
  );
}

function WhiteLogo({ name, domain, className = "" }) {
  const [failed, setFailed] = useState(!domain);
  if (failed) {
    return (
      <span className={`font-display uppercase tracking-wide text-white/75 text-center leading-none ${className}`}>{name}</span>
    );
  }
  return (
    <img
      src={`https://logo.clearbit.com/${domain}?size=200`}
      alt={name}
      loading="lazy"
      onError={() => setFailed(true)}
      className={`object-contain opacity-70 hover:opacity-100 transition-opacity duration-300 ${className}`}
      style={{ filter: "brightness(0) invert(1)" }}
    />
  );
}

export default function Partners() {
  const { t } = useLanguage();
  return (
    <section className="relative z-10 py-28 md:py-36 border-t border-y5-line">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="text-center mb-16">
          <span className="text-y5-yellow text-sm uppercase tracking-[0.3em]">{t.partners.kicker}</span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-7xl tracking-tight uppercase mt-4">{t.partners.title}</h2>
        </div>

        {/* Client testimonials with logo */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
          {CLIENT_TESTIMONIALS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08} className="border border-y5-line bg-y5-surface p-8 flex flex-col">
              <div className="h-14 flex items-center mb-6">
                <ClientLogo src={c.logo} alt={c.name} className="max-h-12 max-w-[160px]" />
              </div>
              <p className="text-white/80 leading-relaxed text-sm italic flex-1">“{c.quote}”</p>
            </Reveal>
          ))}
        </div>

        {/* Logo wall */}
        <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-y5-line">
          {PARTNERS.map((p) => (
            <div key={p.name} data-testid={`partner-${p.name}`} className="h-28 flex items-center justify-center px-6 border-r border-b border-y5-line">
              <WhiteLogo name={p.name} domain={p.domain} className="max-h-10 max-w-[150px] text-lg" />
            </div>
          ))}
          <Link to="/contact" data-testid="partner-cta" className="h-28 flex flex-col items-center justify-center px-6 border-r border-b border-y5-line group">
            <span className="font-display text-2xl uppercase tracking-tight text-y5-yellow leading-none text-center group-hover:scale-105 transition-transform">
              {t.partners.yourBrand[0]}<br />{t.partners.yourBrand[1]}
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}
